import { inject } from '@angular/core';
import { PatientControllerv2Service } from '@app/generated-api';
import { PatientNote } from '@models/patient';
import { RequestNote } from '@models/request';
import { SiteNote } from '@models/site';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { AdminRequestServices } from '@services/admin/admin-request.service';
import { AdminSiteServices } from '@services/admin/admin-site.service';
import { tap } from 'rxjs';
import { NoteCardElement } from './note-card-element';
import { NoteCardFormGroup } from './note-card.formgroup';

type NotesState = {
    patientNotes: NoteCardElement[] | null;
    lastUpdatedPatientNote: NoteCardElement | null;
    siteNotes: NoteCardElement[] | null;
    lastUpdatedSiteNote: NoteCardElement | null;
    requestNotes: NoteCardElement[] | null;
    lastUpdatedRequestNote: NoteCardElement | null;
    patientNotesFormGroup: NoteCardFormGroup | null;
    siteNotesFormGroup: NoteCardFormGroup | null;
    requestNotesFormGroup: NoteCardFormGroup | null;
};

const initialState: NotesState = {
    patientNotes: null,
    lastUpdatedPatientNote: null,
    siteNotes: null,
    lastUpdatedSiteNote: null,
    requestNotes: null,
    lastUpdatedRequestNote: null,
    patientNotesFormGroup: null,
    siteNotesFormGroup: null,
    requestNotesFormGroup: null,
};

export const NoteStore = signalStore(
    withState(initialState),
    withMethods(
        (
            store,
            patientService = inject(PatientControllerv2Service),
            siteService = inject(AdminSiteServices),
            requestService = inject(AdminRequestServices)
        ) => ({
            setPatientNotes(notes: PatientNote[]) {
                patchState(store, { patientNotes: notes });
            },
            setPatientNotesFormGroup(formGroup: NoteCardFormGroup) {
                patchState(store, { patientNotesFormGroup: formGroup });
            },
            setSiteNotes(notes: SiteNote[]) {
                patchState(store, { siteNotes: notes });
            },
            setSiteNotesFormGroup(formGroup: NoteCardFormGroup) {
                patchState(store, { siteNotesFormGroup: formGroup });
            },
            setRequestNotes(notes: RequestNote[]) {
                patchState(store, { requestNotes: notes });
            },
            setRequestNotesFormGroup(formGroup: NoteCardFormGroup) {
                patchState(store, { requestNotesFormGroup: formGroup });
            },
            updatePatientNote(patientId: number, patientNote: PatientNote) {
                return patientService
                    .patientControllerv2UpdatePatientSingleNote(patientId, patientNote.id, { note: patientNote.note })
                    .pipe(
                        tap((note) => {
                            if (note && !!store.patientNotes()) {
                                patchState(store, {
                                    lastUpdatedPatientNote: {
                                        id: note.id,
                                        note: note.note,
                                        modifiedInitials: note.modifiedInitials,
                                        modifiedTime: note.modifiedTime,
                                    },
                                });
                            }
                        })
                    );
            },
            updateSiteNote(siteId: number, siteNote: SiteNote) {
                return siteService.updateSiteNote(siteId, siteNote.id, siteNote.note).pipe(
                    tap((note) => {
                        if (note && !!store.siteNotes()) {
                            patchState(store, { lastUpdatedSiteNote: note });
                        }
                    })
                );
            },
            updateRequestNote(requestId: number, requestNote: RequestNote) {
                //todo //replace request note with the response from the request service
                patchState(store, { lastUpdatedRequestNote: requestNote });
            },
        })
    )
);
